import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import axiosInstance from '../../utils/axiosInstance';
import { getURL } from '../../utils/api';
import RegisterPoolApproveModal from '../../components/RegisterPoolApproveModal';
import Swal from 'sweetalert2';

const STATUS_META = {
  pending: { label: 'Pending', badge: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-400' },
  approved: { label: 'Approved', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-400' },
  rejected: { label: 'Rejected', badge: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-400' },
};

function initialsFromName(name) {
  if (!name) return '?';
  return name.trim().split(/\s+/).filter(Boolean).map((w) => w[0]).slice(0, 2).join('').toUpperCase();
}

function fmtDateTime(value) {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true,
  }).format(new Date(value));
}

function useCountdown(expiresAt) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!expiresAt) return { label: '—', expired: false };
  const diff = new Date(expiresAt).getTime() - now;
  if (diff <= 0) return { label: 'Expired', expired: true };
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return { label: h > 0 ? `${h}h ${m}m` : `${m}m ${s}s`, expired: false };
}

function Avatar({ name }) {
  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-sm font-bold text-indigo-700">
      {initialsFromName(name)}
    </div>
  );
}


export default function RegisterPoolQr() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [approvingSubmission, setApprovingSubmission] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [copied, setCopied] = useState(false);
  const [isQrFullscreen, setIsQrFullscreen] = useState(false);

  const closedRef = useRef(false);
  const qrFullscreenRef = useRef(null);

  // Tracks browser fullscreen state specifically for the QR panel (not the
  // whole page) so it can render a projector-friendly, larger layout while
  // fullscreen and revert automatically on Escape/exit.
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsQrFullscreen(document.fullscreenElement === qrFullscreenRef.current);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handleToggleQrFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      qrFullscreenRef.current?.requestFullscreen?.();
    }
  };

  const joinUrl = useMemo(() => `${window.location.origin}/register-pool/join/${token}`, [token]);
  const countdown = useCountdown(session?.expiresAt);
  const isClosed = session?.status === 'closed' || countdown.expired;

  const loadSubmissions = useCallback(async () => {
    try {
      const resp = await axiosInstance.get(`/admin/register-pool/sessions/${token}/submissions`);
      setSession(resp.data?.session || null);
      setSubmissions(resp.data?.submissions || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load this registration session.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadSubmissions();
    const interval = setInterval(loadSubmissions, 6000);
    return () => clearInterval(interval);
  }, [loadSubmissions]);

  // Best-effort close when the admin navigates away or closes this tab --
  // sendBeacon fires reliably during unload, unlike a normal fetch/axios call.
  useEffect(() => {
    const closeBeacon = () => {
      if (closedRef.current) return;
      const url = `${getURL()}/register-pool/public/sessions/${token}/close`;
      navigator.sendBeacon?.(url);
    };
    window.addEventListener('pagehide', closeBeacon);
    return () => {
      window.removeEventListener('pagehide', closeBeacon);
    };
  }, [token]);

  const handleManualClose = async () => {
    try {
      await axiosInstance.post(`/admin/register-pool/sessions/${token}/close`);
      closedRef.current = true;
      loadSubmissions();
    } catch {
      // non-fatal
    }
  };

  const handleReject = async (submission) => {
    const result = await Swal.fire({
      title: 'Reject this registration?',
      html: `<strong>${submission.fullName}</strong>'s pending registration will be rejected.`,
      icon: 'warning',
      input: 'textarea',
      inputPlaceholder: 'Optional reason…',
      inputAttributes: { 'aria-label': 'Optional reason' },
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, reject it',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    setBusyId(submission.id);
    try {
      await axiosInstance.post(`/admin/register-pool/submissions/${submission.id}/reject`, { reason: result.value || null });
      loadSubmissions();
    } catch (err) {
      Swal.fire({
        title: 'Failed to reject',
        text: err?.response?.data?.message || 'Failed to reject this submission.',
        icon: 'error',
        confirmButtonColor: '#4f46e5',
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(joinUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0 };
    submissions.forEach((s) => { c[s.status] = (c[s.status] || 0) + 1; });
    return c;
  }, [submissions]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 text-slate-400">
        <svg className="h-8 w-8 animate-spin text-indigo-500" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-50 px-6 text-center text-sm text-slate-500">
        <p>{error}</p>
        <button onClick={() => navigate('/admin/register-pool')} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white">
          Back to Register Pool
        </button>
      </div>
    );
  }

  const batchLabel = session?.whatsappGroupName || session?.batchInternalName || session?.batchName;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white px-6 py-4 shadow-sm">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">Register Pool · Live QR Session</p>
            <h1 className="text-lg font-bold text-slate-900">{batchLabel}</h1>
            {session?.courseName && <p className="text-xs text-slate-500">{session.courseShortName || session.courseName}</p>}
          </div>
          <div className="flex items-center gap-3">
            <div className={`rounded-lg border px-3 py-1.5 text-xs font-semibold ${isClosed ? 'border-red-200 bg-red-50 text-red-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
              {isClosed ? 'Closed' : `Live · Expires in ${countdown.label}`}
            </div>
            {!isClosed && (
              <button
                onClick={handleManualClose}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
              >
                Close Registration
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[380px_1fr]">
          {/* QR card */}
          <div className="h-fit rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
            {isClosed ? (
              <div className="flex flex-col items-center gap-3 py-10">
                <svg className="h-12 w-12 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
                <p className="text-sm font-medium text-slate-500">This registration link is closed.<br />Students can no longer submit their details.</p>
              </div>
            ) : (
              <>
                <div
                  ref={qrFullscreenRef}
                  className={isQrFullscreen
                    ? 'flex h-full w-full flex-col items-center justify-center bg-white p-8'
                    : ''}
                >
                  {isQrFullscreen && (
                    <div className="mb-8 text-center">
                      <p className="text-lg font-semibold uppercase tracking-wide text-indigo-600">{batchLabel}</p>
                      {session?.courseName && <p className="mt-1 text-sm text-slate-500">{session.courseShortName || session.courseName}</p>}
                    </div>
                  )}
                  <div className={`mx-auto flex w-fit items-center justify-center rounded-xl border-4 border-slate-100 bg-white ${isQrFullscreen ? 'p-8' : 'p-4'}`}>
                    <QRCodeSVG value={joinUrl} size={isQrFullscreen ? 480 : 220} level="M" includeMargin={false} />
                  </div>
                  <p className={isQrFullscreen ? 'mt-8 text-2xl font-semibold text-slate-700' : 'mt-4 text-sm font-semibold text-slate-700'}>
                    Scan to register for this batch
                  </p>
                  {isQrFullscreen && (
                    <div className="mt-3 flex items-center gap-1.5 text-xs font-medium text-slate-400">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                      {counts.approved} registered
                      {counts.pending > 0 ? ` · ${counts.pending} pending` : ''}
                    </div>
                  )}
                  {isQrFullscreen && (
                    <button
                      onClick={handleToggleQrFullscreen}
                      className="mt-6 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-500 hover:bg-slate-50"
                    >
                      Exit Full Screen
                    </button>
                  )}
                </div>
                {!isQrFullscreen && (
                  <>
                    <div className="mt-3 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <span className="min-w-0 flex-1 truncate text-left text-xs text-slate-500">{joinUrl}</span>
                      <button
                        onClick={handleCopyLink}
                        className={`shrink-0 rounded-md px-2 py-1 text-[11px] font-semibold transition ${copied ? 'bg-emerald-100 text-emerald-700' : 'bg-white text-indigo-600 hover:bg-indigo-50'}`}
                      >
                        {copied ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    <button
                      onClick={handleToggleQrFullscreen}
                      className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-indigo-200 bg-white px-4 py-2 text-sm font-semibold text-indigo-600 hover:bg-indigo-50"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V6a2 2 0 012-2h2M4 16v2a2 2 0 002 2h2m8-16h2a2 2 0 012 2v2m-4 12h2a2 2 0 002-2v-2" />
                      </svg>
                      Full Screen (for projector)
                    </button>
                  </>
                )}
              </>
            )}
          </div>

          {/* Live submissions */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
              <h2 className="text-sm font-bold text-slate-800">Registrations</h2>
              <div className="flex items-center gap-2 text-xs font-semibold">
                <span className="rounded-full bg-amber-50 px-2.5 py-1 text-amber-700">{counts.pending} pending</span>
                <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-emerald-700">{counts.approved} approved</span>
                <span className="rounded-full bg-red-50 px-2.5 py-1 text-red-700">{counts.rejected} rejected</span>
              </div>
            </div>

            {submissions.length === 0 ? (
              <div className="flex flex-col items-center gap-2 px-5 py-16 text-center text-sm text-slate-400">
                <svg className="h-10 w-10 text-slate-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                </svg>
                Waiting for students to scan and register…
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {submissions.map((s) => {
                  const meta = STATUS_META[s.status] || STATUS_META.pending;
                  return (
                    <li key={s.id} className="flex flex-wrap items-center gap-4 px-5 py-4">
                      <Avatar name={s.fullName} />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-semibold text-slate-900">{s.fullName}</span>
                          <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${meta.badge}`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
                            {meta.label}
                          </span>
                        </div>
                        <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-500">
                          {s.studentId && <span className="font-semibold text-indigo-600">{s.studentId}</span>}
                          {s.registrationNumber && <span>Reg #: {s.registrationNumber}</span>}
                          {s.telephone && <span>{s.telephone}</span>}
                          {s.nicNumber && <span>NIC: {s.nicNumber}</span>}
                          {s.email && <span className="truncate">{s.email}</span>}
                          <span>Submitted {fmtDateTime(s.submittedAt)}</span>
                        </div>
                        {s.status === 'approved' && s.createdStudentId && (
                          <p className="mt-1 text-xs font-medium text-emerald-600">
                            Registered as {s.createdStudentId} by {s.approvedByName || 'staff'} · {fmtDateTime(s.approvedAt)}
                          </p>
                        )}
                        {s.status === 'rejected' && (
                          <p className="mt-1 text-xs font-medium text-red-500">
                            Rejected by {s.approvedByName || 'staff'} · {fmtDateTime(s.approvedAt)}
                            {s.reviewNote ? ` — "${s.reviewNote}"` : ''}
                          </p>
                        )}
                      </div>
                      {s.status === 'pending' && (
                        <div className="flex shrink-0 gap-2">
                          <button
                            onClick={() => setApprovingSubmission(s)}
                            disabled={busyId === s.id}
                            className="rounded-lg bg-emerald-100 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-200 disabled:opacity-50"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleReject(s)}
                            disabled={busyId === s.id}
                            className="rounded-lg bg-red-100 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-200 disabled:opacity-50"
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </main>

      {approvingSubmission && (
        <RegisterPoolApproveModal
          submission={approvingSubmission}
          batchId={session?.batchId}
          onClose={() => setApprovingSubmission(null)}
          onApproved={() => {
            setApprovingSubmission(null);
            loadSubmissions();
          }}
        />
      )}
    </div>
  );
}
